import { template as template_ff6650074fdc4739bc8590faf5040d06 } from "@ember/template-compiler";
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { waitFor } from '@ember/test-waiters';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import { task } from 'ember-concurrency';
import { sortBy } from '@eflexsystems/ember-composable-helpers';
import StandardModal from 'eflex/components/standard-modal';
import EmberGrid from 'eflex/components/ember-grid';
import BsButton from 'eflex/components/bs-button';
import BsForm from 'eflex/components/bs-form';
import SpinnerOverlay from 'eflex/components/spinner-overlay';
class KineticMaterialsModal extends Component {
    @service
    kineticRepo;
    @service
    intl;
    @service
    eflexAjax;
    @service
    notifier;
    @tracked
    selectedMaterial;
    @tracked
    selectedWarehouse;
    @tracked
    selectedBin;
    @tracked
    selectedUOM;
    @tracked
    quantityToIssue = 0;
    @tracked
    issuedComplete;
    @tracked
    availableWarehouses;
    @tracked
    availableBins;
    @tracked
    availableUOM;
    @tracked
    displayAllBins = false;
    get isRunning() {
        return this.onStartIssue.isRunning || this.getAvailableBins.isRunning || this.onIssue.isRunning;
    }
    get submitDisabled() {
        return isEmpty(this.selectedMaterial) || isEmpty(this.selectedWarehouse) || isEmpty(this.selectedBin) || isEmpty(this.quantityToIssue) || this.isRunning;
    }
    clearWarehouseBins = ()=>{
        Object.assign(this, {
            selectedWarehouse: null,
            selectedBin: null,
            availableBins: null
        });
    };
    clearValues = ()=>{
        this.clearWarehouseBins();
        Object.assign(this, {
            selectedMaterial: null,
            selectedUOM: null,
            quantityToIssue: 0,
            issuedComplete: null,
            availableUOM: null
        });
    };
    onCancel = ()=>{
        this.clearValues();
        this.args.onCancel();
    };
    onStartIssue = task(waitFor(async (material)=>{
        try {
            this.availableWarehouses = await this.eflexAjax.get.perform('kinetic/material', {
                action: 'getWarehouses',
                materialPartNum: material.partNumber
            });
            this.selectedMaterial = material;
            const assemblyOperation = this.args.assemblyOperation;
            const { defaultUOM, UOMList } = await this.eflexAjax.get.perform('kinetic/material', {
                action: 'getUOM',
                jobNum: assemblyOperation.job.jobNumber,
                assemblySequence: assemblyOperation.assembly.assemblySequence,
                materialSequence: this.selectedMaterial.sequence,
                materialPartNum: this.selectedMaterial.partNumber
            });
            this.selectedUOM = defaultUOM;
            this.availableUOM = UOMList;
        } catch (error) {
            if (error.isValidationError && error.message != null) {
                this.notifier.sendError(error);
            } else {
                console.error(error);
            }
        }
    }));
    getAvailableBins = task(waitFor(async ()=>{
        try {
            Object.assign(this, {
                selectedBin: null,
                availableBins: null
            });
            this.availableBins = await this.eflexAjax.get.perform('kinetic/material', {
                action: 'getBins',
                materialPartNum: this.selectedMaterial.partNumber,
                warehouseCode: this.selectedWarehouse.warehouseCode,
                UOMCode: this.selectedUOM.UOMCode,
                displayAllBins: this.displayAllBins
            });
        } catch (error) {
            if (error.isValidationError && error.message != null) {
                this.notifier.sendError(error);
            } else {
                console.error(error);
            }
        }
    }));
    onSelectWarehouse = task(waitFor(async (warehouse)=>{
        this.selectedWarehouse = warehouse;
        await this.getAvailableBins.perform();
    }));
    toggleDisplayAllBins = task(waitFor(async (displayAllBins)=>{
        this.displayAllBins = displayAllBins;
        await this.getAvailableBins.perform();
    }));
    onSelectBin = (bin)=>{
        this.selectedBin = bin;
    };
    onSelectUOM = (uom)=>{
        this.clearWarehouseBins();
        this.selectedUOM = uom;
    };
    onIssue = task(waitFor(async (material)=>{
        try {
            const assemblyOperation = this.args.assemblyOperation;
            await this.eflexAjax.post.perform('kinetic/material', {
                jobNum: assemblyOperation.job.jobNumber,
                assemblySequence: assemblyOperation.assembly.assemblySequence,
                materialSequence: material.sequence,
                quantity: this.quantityToIssue
            });
            this.clearValues();
        } catch (error) {
            if (error.isValidationError && error.message != null) {
                this.notifier.sendError(error);
            } else {
                console.error(error);
            }
        }
    }));
    static{
        template_ff6650074fdc4739bc8590faf5040d06(`
    <StandardModal
      class="test-kinetic-materials-modal"
      @size="lg"
      @onCancel={{this.onCancel}}
      @darkMode={{true}}
      as |modal|>
      <modal.header class="pb-2" as |header|>
        <div class="d-flex align-items-center w-100 mb-3">
          <h5 class="modal-title lh-sm">
            {{t 'kinetic.materials.issueMaterial'}}
          </h5>
          <header.close />
        </div>

        <div class="row w-100 mb-3 ms-n1">
          <div class="col-2">
            <label>
              {{t "job"}}
            </label>
            <div class="fw-bold test-material-job">
              {{@assemblyOperation.job.jobNumber}}
            </div>
          </div>
          <div class="col-2">
            <label>
              {{t "kinetic.assembly"}}
            </label>
            <div class="fw-bold test-material-assembly">
              {{@assemblyOperation.assembly.assemblySequence}}
            </div>
          </div>
          <div class="col-2">
            <label>
              {{t "operation"}}
            </label>
            <div class="fw-bold test-material-operation">
              {{@assemblyOperation.name}}
            </div>
          </div>
        </div>

        {{#if (not (isEmpty this.selectedMaterial))}}
          <div class="row w-100 mb-3 ms-n1">
            <div class="col-2">
              <label>
                {{t 'kinetic.sequence'}}
              </label>
              <div class="fw-bold test-material-sequence">
                {{this.selectedMaterial.sequence}}
              </div>
            </div>
            <div class="col-2">
              <label>
                {{t 'kinetic.materials.part'}}
              </label>
              <div class="fw-bold test-material-part-number">
                {{this.selectedMaterial.partNumber}}
              </div>
            </div>
            <div class="col-2">
              <label>
                {{t 'kinetic.materials.description'}}
              </label>
              <div class="fw-bold test-material-description">
                {{this.selectedMaterial.partDescription}}
              </div>
            </div>
            <div class="col">
              <label>
                {{t 'kinetic.materials.issuedRequired'}}
              </label>
              <div class="fw-bold test-material-quantity-issued">
                {{this.selectedMaterial.quantityIssued}}
              </div>
            </div>
            <div class="col">
              <label>
                {{t 'kinetic.issuedComplete'}}
              </label>
              <div class="fw-bold test-material-issued-complete">
                {{this.selectedMaterial.issuedComplete}}
              </div>
            </div>
            <div class="col">
              <BsButton
                class="w-100 mt-2 test-material-change"
                @type="outline-secondary"
                @disabled={{this.isRunning}}
                @onClick={{this.clearValues}}
              >
                {{t 'kinetic.materials.change'}}
              </BsButton>
            </div>
          </div>
        {{/if}}
      </modal.header>

      {{#if (isEmpty this.selectedMaterial)}}
        <modal.body class="main-overflow">
          <EmberGrid class="w-100 ember-grid-dark">
            <thead>
              <tr>
                <th>
                  {{t 'kinetic.sequence'}}
                </th>
                <th>
                  {{t 'kinetic.materials.part'}}
                </th>
                <th>
                  {{t 'kinetic.materials.description'}}
                </th>
                <th>
                  {{t 'kinetic.materials.issuedRequired'}}
                </th>
                <th>
                  {{t 'kinetic.issuedComplete'}}
                </th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {{#if (isEmpty @assemblyOperation.materials)}}
                <tr>
                  <td colspan="4" class="center">
                    {{t 'kinetic.noMaterialsAssigned'}}
                  </td>
                </tr>
              {{else}}
                {{#each (sortBy "sequence" @assemblyOperation.materials) as |material|}}
                  <tr class="test-material-row">
                    <td class="test-material-sequence">{{material.sequence}}</td>
                    <td class="test-material-part-number">{{material.partNumber}}</td>
                    <td class="test-material-description">{{material.partDescription}}</td>
                    <td class="test-material-quantity-issued">{{material.quantityIssued}}</td>
                    <td class="test-material-issued-complete">{{material.issuedComplete}}</td>
                    <td>
                      <BsButton
                        @type="outline-secondary"
                        class="w-100 test-material-issue"
                        @disabled={{this.isRunning}}
                        @onClick={{this.onStartIssue.perform material}}
                      >
                        {{t 'kinetic.materials.issue'}}
                      </BsButton>
                    </td>
                  </tr>
                {{/each}}
              {{/if}}
            </tbody>
          </EmberGrid>
        </modal.body>
        <modal.footer as |footer|>
          <footer.close>
            {{t "close"}}
          </footer.close>
        </modal.footer>
      {{else}}
        <modal.body class="main-overflow">
          <BsForm class="form-dark" @model={{this}} as |form|>
            <div class="row mb-4">
              <div class="col-3">
                <form.element
                  @controlType="number"
                  @label={{t 'kinetic.materials.quantityToIssue'}}
                  @property="quantityToIssue"
                  @outline={{true}}
                  @disabled={{this.isRunning}}
                  class="test-quantity-to-issue"
                  autocomplete="off"
                />
              </div>
              <div class="col-3">
                <form.element
                  class="test-uom"
                  @controlType="power-select"
                  @outline={{true}}
                  @label={{t 'kinetic.uom'}}
                  @options={{this.availableUOM}}
                  @value={{this.selectedUOM}}
                  @onChange={{this.onSelectUOM}}
                as |el|>
                  <el.control
                    @title="{{this.selectedUOM.UOMCode}} - {{this.selectedUOM.UOMDesc}}"
                    @disabled={{this.isRunning}}
                  as |opt|>
                    {{opt.UOMCode}} - {{opt.UOMDesc}}
                  </el.control>
                </form.element>
              </div>
              <div class="col-6 position-relative">
                <form.element
                  @controlType="checkbox"
                  @label={{t 'kinetic.issuedComplete'}}
                  @property="issuedComplete"
                  @disabled={{this.isRunning}}
                  class="test-issued-complete position-absolute bottom-0 start-0 ms-3 mb-2"
                />
              </div>
            </div>

            <div class="row mb-1">
              <div class="col">
                <label class="fw-bold">
                  {{t 'kinetic.materials.location'}}
                </label>
              </div>
            </div>

            <div class="row">
              <div class="col-6">
                <form.element
                  class="test-warehouse"
                  @controlType="power-select"
                  @outline={{true}}
                  @label={{t 'kinetic.materials.warehouse'}}
                  @options={{this.availableWarehouses}}
                  @optionLabelPath="description"
                  @value={{this.selectedWarehouse}}
                  @onChange={{this.onSelectWarehouse.perform}}
                  @noMatchesMessage={{t "noMatch"}}
                as |el|>
                  <el.control
                    @placeholder={{t "kinetic.materials.selectWarehouse"}}
                    @searchEnabled={{true}}
                    @searchField="description"
                    @disabled={{this.isRunning}}
                  />
                </form.element>
              </div>
              <div class="col-6">
                <form.element
                  class="test-bin"
                  @controlType="power-select"
                  @outline={{true}}
                  @label={{t 'kinetic.materials.bin'}}
                  @options={{this.availableBins}}
                  @optionLabelPath="binDesc"
                  @value={{this.selectedBin}}
                  @onChange={{this.onSelectBin}}
                  @noMatchesMessage={{t "noMatch"}}
                as |el|>
                  <el.control
                    @placeholder={{t "kinetic.materials.selectBin"}}
                    @searchEnabled={{true}}
                    @searchField="binDesc"
                    @disabled={{this.isRunning}}
                  />
                </form.element>
              </div>
            </div>
            <div class="row mb-4">
              <div class="col-6" />
              <div class="col-6 position-relative">
                <form.element
                  @controlType="checkbox"
                  @label={{t 'kinetic.materials.displayAllBins'}}
                  @value={{@displayAllBins}}
                  @onChange={{this.toggleDisplayAllBins.perform}}
                  @disabled={{this.isRunning}}
                  class="test-display-all-bins"
                />
              </div>
            </div>
          </BsForm>
        </modal.body>
        <modal.footer as |footer|>
          <footer.confirm
            @onConfirm={{this.onIssue.perform this.selectedMaterial}}
            @disabled={{this.submitDisabled}}
          >
            {{t "submit"}}
          </footer.confirm>
          <footer.close />
        </modal.footer>
      {{/if}}
    </StandardModal>

    {{#if this.isRunning}}
      <SpinnerOverlay @message={{t "kinetic.loading.wait"}} />
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
export default KineticMaterialsModal;
